import React, { useState, useEffect } from 'react'

import Toast, { TOAST_TYPE } from 'components/Toast/Toast'

import FormModal from 'components/Modal/FormModal/FormModal'
import FormBlock from 'components/Forms/FormBlock'
import Input from 'components/Forms/Input/Input'
import Toggle from 'components/Forms/Toggle/Toggle'
import JsonCodeBlock from 'components/Forms/CodeBlock/JSON/JSON'
import Upload from 'components/Forms/Upload/Upload'
import Preloader from 'components/Preloader/Preloader'

import { fieldsValidator, getIsValueRule, getIsNotEmptyStringRule, getIsJsonStringRule } from 'utils/fieldsValidator'
import { encodeOrDecodeJsonStructure, removeKeyFromObject, setKeyToObject } from 'utils/common'

import API from 'api'

import { PROJECTS_STATUSES } from 'constants/projects'

const EditProjectModal = ({ projectId, onRefresh = () => {}, onClose = () => {} }) => {
    const [isReady, setIsReady] = useState(false)

    const [project, setProject] = useState(false)

    const [name, setName] = useState('')
    const [title, setTitle] = useState('')
    const [description, setDescription] = useState('')
    const [projectStructureJson, setProjectStructureJson] = useState('')

    const [preview, setPreview] = useState(null)
    const [previewUrl, setPreviewUrl] = useState('')

    const [mobilePreview, setMobilePreview] = useState(null)
    const [mobilePreviewUrl, setMobilePreviewUrl] = useState('')

    const [isPublishRepublish, setIsPublishRepublish] = useState(false)

    const [errors, setErrors] = useState({})

    useEffect(() => {
        API.PROJECTS.GET_PROJECT(projectId).then(project => {
            setProject(project)

            setName(project.name)
            setTitle(project.title)
            setDescription(project.description)
            setIsPublishRepublish(project.projectStatus === PROJECTS_STATUSES.PUBLISHED)
            setProjectStructureJson(JSON.stringify(project.projectStructureJson))

            setIsReady(true)
        })
    }, [])

    const handleSubmit = async () => {
        try {
            setErrors({})

            const validator = new fieldsValidator()

            validator.addFields([
                validator.createField('projectStructureJson', projectStructureJson, [
                    getIsValueRule(),
                    getIsNotEmptyStringRule(),
                    getIsJsonStringRule(),
                ]),
            ])

            if (preview) {
                validator.addFields([
                    validator.createField('preview', previewUrl, [
                        getIsValueRule(),
                        getIsNotEmptyStringRule('File must be uploaded'),
                    ]),
                ])
            }
            if (mobilePreview) {
                validator.addFields([
                    validator.createField('mobilePreview', mobilePreviewUrl, [
                        getIsValueRule(),
                        getIsNotEmptyStringRule('File must be uploaded'),
                    ]),
                ])
            }

            const { isHasErrors, errors } = validator.validate()
            if (isHasErrors) {
                setErrors(errors)
                return
            }

            await API.PROJECTS.UPDATE_PROJECT(project.projectId, {
                ...project,
                name,
                title,
                description,
                previewImageUrl: previewUrl || project.previewUrl,
                mobilePreviewImageUrl: mobilePreviewUrl || project.mobilePreviewUrl,
                projectStructureJson: encodeOrDecodeJsonStructure(JSON.parse(projectStructureJson), 'encode'),
            })

            if (isPublishRepublish) {
                await API.PROJECTS.PUBLISH_PROJECT(project.projectId)
            }

            await onRefresh()

            return true
        } catch (err) {
            console.error(err)
            Toast(TOAST_TYPE.ERROR)
            throw err
        }
    }

    return (
        <FormModal
            headText="Edit project"
            successText="Project updated successfully!"
            actionText="Update"
            onAction={() => handleSubmit()}
            onClose={() => onClose()}
        >
            {isReady ? (
                <>
                    <FormBlock label="Name">
                        <Input
                            value={name}
                            error={errors.name}
                            onChange={value => {
                                setName(value)
                                setErrors(removeKeyFromObject(errors, 'name'))
                            }}
                        />
                    </FormBlock>
                    <FormBlock label="Project structure (JSON)">
                        <JsonCodeBlock
                            value={projectStructureJson}
                            error={errors.projectStructureJson}
                            onChange={value => {
                                setProjectStructureJson(value)
                                setErrors(removeKeyFromObject(errors, 'projectStructureJson'))
                            }}
                        />
                    </FormBlock>
                    <FormBlock label="Title">
                        <Input
                            value={title}
                            error={errors.title}
                            onChange={value => {
                                setTitle(value)
                                setErrors(removeKeyFromObject(errors, 'title'))
                            }}
                        />
                    </FormBlock>
                    <FormBlock label="Description">
                        <Input
                            value={description}
                            error={errors.description}
                            onChange={value => {
                                setDescription(value)
                                setErrors(removeKeyFromObject(errors, 'description'))
                            }}
                        />
                    </FormBlock>
                    <FormBlock label="Preview image">
                        <Upload
                            lastUrl={project.previewUrl}
                            value={preview}
                            error={errors.preview}
                            isUploaded={!!previewUrl}
                            onReset={() => {
                                setPreviewUrl('')
                                setPreview(null)
                            }}
                            onUpload={({ isSuccess, url, errorText }) => {
                                if (!isSuccess) {
                                    setErrors(setKeyToObject(errors, 'preview', errorText))
                                    return
                                }
                                setPreviewUrl(url)
                                setErrors(removeKeyFromObject(errors, 'preview'))
                            }}
                            onChange={file => {
                                setPreviewUrl('')
                                setPreview(file)
                                setErrors(removeKeyFromObject(errors, 'preview'))
                            }}
                        />
                    </FormBlock>
                    <FormBlock label="Preview image (Mobile)">
                        <Upload
                            lastUrl={project.mobilePreviewUrl}
                            value={mobilePreview}
                            error={errors.mobilePreview}
                            isUploaded={!!mobilePreviewUrl}
                            onReset={() => {
                                setMobilePreviewUrl('')
                                setMobilePreview(null)
                            }}
                            onUpload={({ isSuccess, url, errorText }) => {
                                if (!isSuccess) {
                                    setErrors(setKeyToObject(errors, 'mobilePreview', errorText))
                                    return
                                }
                                setMobilePreviewUrl(url)
                                setErrors(removeKeyFromObject(errors, 'mobilePreview'))
                            }}
                            onChange={file => {
                                setMobilePreviewUrl('')
                                setMobilePreview(file)
                                setErrors(removeKeyFromObject(errors, 'mobilePreview'))
                            }}
                        />
                    </FormBlock>
                    <br />
                    <FormBlock>
                        <Toggle
                            value={isPublishRepublish}
                            label={project.projectStatus === PROJECTS_STATUSES.PUBLISHED ? 'Republish' : 'Publish'}
                            onChange={value => {
                                setIsPublishRepublish(value)
                                setErrors(removeKeyFromObject(errors, 'isPublishRepublish'))
                            }}
                        />
                    </FormBlock>
                </>
            ) : (
                <Preloader />
            )}
        </FormModal>
    )
}

export default EditProjectModal
